import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { WebPageSchema, BreadcrumbListSchema, ItemListSchema } from '@/components/base/SchemaOrg';
import { categories, getMoviesByCategory, type MovieItem } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'movie');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'views'>('rating');

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeTab);
  const items = useMemo(() => {
    let list = getMoviesByCategory(activeTab);
    if (sortBy === 'rating') return [...list].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'year') return [...list].sort((a, b) => b.year - a.year);
    if (sortBy === 'views') return [...list].sort((a, b) => parseInt(b.views) - parseInt(a.views));
    return list;
  }, [activeTab, sortBy]);

  return (
    <>
      <ScrollToTop />
      <WebPageSchema
        title={`${currentCategory?.label || '分类'} - 黄瓜影视在线看`}
        description={`黄瓜影视${currentCategory?.label || '影视'}分类，海量高清${currentCategory?.label || ''}资源免费在线观看，涵盖热门、经典、最新${currentCategory?.label || '内容'}。`}
        url={`https://huanggua.tv/category/${activeTab}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://huanggua.tv' },
          { name: currentCategory?.label || '分类', url: `https://huanggua.tv/category/${activeTab}` },
        ]}
      />
      <ItemListSchema
        items={items.map((item, index) => ({
          name: item.title,
          url: `https://huanggua.tv/article/${item.id}`,
          position: index + 1,
        }))}
      />

      <div className="min-h-screen" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <Navbar />

        <main className="pt-20 pb-12">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors" style={{ color: 'oklch(var(--foreground-400))' }}>首页</Link>
              <span style={{ color: 'oklch(var(--foreground-300))' }}>/</span>
              <span style={{ color: 'oklch(var(--foreground-700))' }}>{currentCategory?.label}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-bold font-heading mb-6" style={{ color: 'oklch(var(--foreground-950))' }}>
              {currentCategory?.label}
              <span className="text-sm font-normal ml-3" style={{ color: 'oklch(var(--foreground-400))' }}>
                共 {items.length} 部
              </span>
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-1.5 p-1 rounded-full" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveTab(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === cat.slug
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'hover:text-foreground-950'
                    }`}
                    style={activeTab !== cat.slug ? { color: 'oklch(var(--foreground-500))' } : {}}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.label}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'oklch(var(--foreground-400))' }}>排序：</span>
                {[
                  { key: 'rating', label: '评分最高' },
                  { key: 'year', label: '最新上映' },
                  { key: 'views', label: '播放最多' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key as typeof sortBy)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'text-white'
                        : ''
                    }`}
                    style={sortBy === opt.key ? { backgroundColor: 'oklch(var(--secondary-500))' } : { backgroundColor: 'oklch(var(--background-100))', color: 'oklch(var(--foreground-500))' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {items.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {items.map((item: MovieItem, index: number) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group cursor-pointer animate-scale-in"
                    style={{ animationDelay: `${index * 60}ms` }}
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5">
                      <img
                        src={item.poster}
                        alt={item.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-2 right-2">
                        <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-accent-500/90 text-white">
                          {item.rating}
                        </span>
                      </div>
                      {item.isNew && (
                        <div className="absolute top-2 left-2">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-secondary-500/80 text-white">NEW</span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-medium truncate group-hover:text-primary-500 transition-colors" style={{ color: 'oklch(var(--foreground-800))' }}>
                      {item.title}
                    </h3>
                    <p className="text-xs mt-0.5" style={{ color: 'oklch(var(--foreground-400))' }}>
                      {item.year} · {item.genre.slice(0, 2).join(' / ')}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
                  <i className="ri-movie-2-line text-2xl" style={{ color: 'oklch(var(--foreground-300))' }}></i>
                </div>
                <p className="text-sm" style={{ color: 'oklch(var(--foreground-400))' }}>暂无内容</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}